/**
 * Notification Types - Single Source of Truth.
 *
 * Synchronized with:
 * - backend/app/services/notifications/types.py
 * - backend/app/schemas/notification.py
 */
export const NotificationType = {
  // === User Notifications ===

  // Chat & Social
  MESSAGE: 'message',
  PROFILE_VISIT: 'profile_visit',
  MATCH: 'match',
  LIKE: 'like',

  // Friendship
  FRIEND_REQUEST_RECEIVED: 'friend_request_received',
  FRIEND_REQUEST_ACCEPTED: 'friend_request_accepted',

  // Billing & Purchases (Legacy/Generic)
  COIN_PURCHASE: 'coin_purchase',
  SUBSCRIPTION: 'subscription',
  LOW_BALANCE: 'low_balance',
  CHECKOUT_RECOVERY: 'checkout_recovery',
  AUTO_RELOAD_SUCCESS: 'auto_reload_success',
  AUTO_RELOAD_FAILED: 'auto_reload_failed',
  PROMOTION: 'promotion',

  // Platform Billing (echtes Geld via Stripe/Payment Provider)
  BILLING_PAYMENT_FAILED: 'billing_payment_failed',
  BILLING_SUBSCRIPTION_DEACTIVATED: 'billing_subscription_deactivated',
  BILLING_PLAN_CHANGED: 'billing_plan_changed',
  BILLING_SUBSCRIPTION_EXPIRING: 'billing_subscription_expiring',

  // Price Change Management
  BILLING_PRICE_CHANGE_ANNOUNCED: 'billing_price_change_announced',
  BILLING_PRICE_CONSENT_REQUIRED: 'billing_price_consent_required',
  BILLING_PRICE_CONSENT_REMINDER: 'billing_price_consent_reminder',
  BILLING_PRICE_CHANGE_APPLIED: 'billing_price_change_applied',
  BILLING_PRICE_CHANGE_REJECTED: 'billing_price_change_rejected',

  // Achievements & Milestones
  ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
  CHALLENGE_COMPLETED: 'challenge_completed',
  SUBSCRIPTION_EXPIRING: 'subscription_expiring',
  BIRTHDAY: 'birthday',

  // Gifts
  GIFT_RECEIVED: 'gift_received',

  // Characters
  NEW_CHARACTER: 'new_character',
  CHARACTER_ONLINE: 'character_online',

  // Creator Space
  CREATOR_NEW_POST: 'creator_new_post',
  CREATOR_POST_COMMENT: 'creator_post_comment',
  CREATOR_POST_COMMENT_REPLY: 'creator_post_comment_reply',

  // Creator Subscriptions (Coin-basiert)
  CREATOR_SUBSCRIPTION_NEW: 'creator_subscription_new',
  CREATOR_SUBSCRIPTION_EXPIRING: 'creator_subscription_expiring',
  CREATOR_SUBSCRIPTION_LOW_BALANCE: 'creator_subscription_low_balance',
  CREATOR_SUBSCRIPTION_CANCELLED_COINS: 'creator_subscription_cancelled_coins',
  CREATOR_SUBSCRIPTION_RENEWED: 'creator_subscription_renewed',

  // System
  SYSTEM: 'system',

  // === Admin Notifications ===
  SYSTEM_ALERT: 'system_alert',
  ADMIN_ACTIVITY: 'admin_activity',
  USER_REPORT: 'user_report',
  PERFORMANCE_WARNING: 'performance_warning',
  CONTENT_FLAGGED: 'content_flagged',
  PAYMENT_ISSUE: 'payment_issue',

  // === Deprecated (fuer Rueckwaertskompatibilitaet mit DB-Eintraegen) ===
  FOLLOW_UP: 'follow_up', // Jetzt MESSAGE mit is_follow_up
  PROFILE_INCOMPLETE: 'profile_incomplete', // Legacy DB entries
} as const;

export type NotificationTypeValue = (typeof NotificationType)[keyof typeof NotificationType];

export interface Notification {
  id: number;
  user_id?: number;
  type: NotificationTypeValue;
  title: string;
  content: string;
  data?: Record<string, unknown> | null;
  is_read: boolean;
  is_pushed?: boolean;
  created_at: string;
  read_at?: string | null;
}

export interface NotificationList {
  notifications: Notification[];
  total: number;
  unread_count: number;
}
